/**
 * The wire shapes `Rws.AiInsights.Host.Administration.RoleAdminEndpoints` returns (phase 21,
 * `AIA-81`, `UR-AD-13`): list roles with their permission bundles, create/rename a role, edit a
 * role's permission grants (all gated on `roles.administer`), and assign/revoke a role on a person
 * (gated on `entitlement.administer`, the same key that already means "assign roles").
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** One role and the permission keys currently granted to it. */
export interface RoleBundle {
  id: string;
  name: string;
  description: string;
  permissionKeys: string[];
  /** True for an automatic role: its members are derived by a rule, so none are added or removed by hand. */
  isAutomatic: boolean;
}

const rolesPath = "/api/entitlement/roles";

function roleByIdPath(id: string): string {
  return `${rolesPath}/${encodeURIComponent(id)}`;
}

function rolePermissionsPath(id: string): string {
  return `${roleByIdPath(id)}/permissions`;
}

function personRolePath(personId: string, roleId: string): string {
  return `/api/users/${encodeURIComponent(personId)}/roles/${encodeURIComponent(roleId)}`;
}

/** One permission the portal recognises, as `GET /api/entitlement/permissions` lists it. */
export interface PermissionDefinition {
  key: string;
  description: string;
}

const permissionsPath = "/api/entitlement/permissions";

/** Calls `GET /api/entitlement/permissions`, so the roles screen can offer every permission, not only those it knows by name. */
export async function fetchPermissions(): Promise<AdminResult<PermissionDefinition[]>> {
  return adminRequest<PermissionDefinition[]>(permissionsPath, { method: "GET" }, "permissions");
}

/** Calls `GET /api/entitlement/roles`. */
export async function fetchRoles(): Promise<AdminResult<RoleBundle[]>> {
  return adminRequest<RoleBundle[]>(rolesPath, { method: "GET" }, "roles");
}

/** Calls `POST /api/entitlement/roles`. */
export async function createRole(name: string, description: string): Promise<AdminResult<RoleBundle>> {
  return adminRequest<RoleBundle>(rolesPath, { method: "POST", body: JSON.stringify({ name, description }) }, "role creation");
}

/** Calls `PUT /api/entitlement/roles/{id}` to rename or redescribe a role. */
export async function renameRole(id: string, name: string, description: string): Promise<AdminResult<RoleBundle>> {
  return adminRequest<RoleBundle>(roleByIdPath(id), { method: "PUT", body: JSON.stringify({ name, description }) }, "role update");
}

/**
 * Calls `PUT /api/entitlement/roles/{id}/permissions` to add and/or remove permission keys from a
 * role's bundle in one request.
 */
export async function setRolePermissions(id: string, add: string[], remove: string[]): Promise<AdminResult<RoleBundle>> {
  return adminRequest<RoleBundle>(rolePermissionsPath(id), { method: "PUT", body: JSON.stringify({ add, remove }) }, "role permission update");
}

/**
 * Calls `POST /api/users/{personId}/roles/{roleId}` to grant a role.
 */
export async function assignRole(personId: string, roleId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(personRolePath(personId, roleId), { method: "POST" }, "role assignment");
}

/**
 * Calls `DELETE /api/users/{personId}/roles/{roleId}` to revoke a role. Returns
 * `{ kind: "conflict" }` — never a generic error — when the API refuses because this would remove
 * the last administrator (`LastAdministratorException`, surfaced as `409`).
 */
export async function revokeRole(personId: string, roleId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(personRolePath(personId, roleId), { method: "DELETE" }, "role revocation");
}

/**
 * Calls `DELETE /api/entitlement/roles/{id}` to delete a role bundle entirely.
 */
export async function deleteRole(id: string): Promise<AdminResult<null>> {
  return adminRequest<null>(roleByIdPath(id), { method: "DELETE" }, "role deletion");
}
